import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';

import { LoginService } from '../../../../pages/login/login.service';
import { AppService, UserService } from '../../../../shared/service/user.service';
import { ConfirmService } from '../../../../shared/components/confirm/confirm.service';
import { AppSubject } from '../../../../shared/app-subject';

interface RunValue {
  idValue?: string;
  idValues?: any;
  actionType: string;
  taxCode?: string;
  requireLoginKey?: boolean;
  signingData?: any;
}

@Injectable({
  providedIn: 'root'
})
export class SigningService {

  readonly errorSubject = new Subject<any>();
  readonly displaySubject = new Subject<any>();

  private runResolve?: (value: void | PromiseLike<void>) => void;
  private runReject?: (reason?: any) => void;

  constructor(
    private http: HttpClient,
    private backend: HttpBackend,
    private confirmService: ConfirmService,
    private loginService: LoginService
  ) {
    this.http = new HttpClient(this.backend);
  }

  run(runValue: RunValue): Promise<void> {
    AppSubject.setReloadData('');

    let signingType = UserService.get(UserService.KEY.SIGNING);
    let signingTypes = UserService.get(UserService.KEY.SIGNING_TYPES);

    if (!signingTypes) {
      const user = UserService.getUser();
      if (user?.signingTypes?.length > 0) {
        signingTypes = JSON.stringify(user.signingTypes);
        UserService.set(UserService.KEY.SIGNING_TYPES, signingTypes);
      } else {
        signingTypes = 'NULL';
        UserService.set(UserService.KEY.SIGNING_TYPES, 'NULL');
      }
    }

    if (signingTypes === 'NULL' && !signingType) {
      signingType = 'UsbTokenSigning';
    }

    if (signingTypes !== 'NULL' && !signingType && signingType !== 'newCert') {
      this.displaySubject.next('form1');
    }

    if (!this.runResolve) {
      return new Promise((resolve, reject) => {
        this.runResolve = (v) => {
          resolve(v);
          this.cleanupResolver();
        };
        this.runReject = (e) => {
          reject(e);
          this.cleanupResolver();
        };
        this.executeRun(runValue, signingType, signingTypes);
      });
    }

    this.executeRun(runValue, signingType, signingTypes);
    return Promise.resolve();
  }

  cancel(): void {
    this.runReject?.('Signing cancelled by user');
    this.cleanupResolver();
    AppSubject.setReloadData('');
  }

  private cleanupResolver(): void {
    this.runResolve = undefined;
    this.runReject = undefined;
  }

  private executeRun(runValue: RunValue, signingType: string, signingTypes: string): void {
    UserService.set(UserService.KEY.RUN_VALUE, JSON.stringify(runValue));
    if (!signingType && signingTypes !== 'NULL') {
      return;
    }
    if (signingType === 'UsbTokenSigning' || signingType === 'newCert' || signingTypes === 'NULL') {
      this.handleUsbSigning(runValue);
      return;
    }
    this.handleRemoteSigningFlow(signingType);
  }

  private async handleUsbSigning(runValue: RunValue): Promise<void> {
    try {
      const testRes: any = await firstValueFrom(this.http.get('http://localhost:13369/test').pipe(timeout(1000)));
      if (testRes?.receiveDataFromBrowser === 'Y') {
        await this.runModernUsbFlow(runValue);
      } else {
        await this.runLegacyUsbFlow(runValue);
      }
    } catch (error: any) {
      if (error.status === 200) {
        await this.runLegacyUsbFlow(runValue);
      } else {
        await this.handleUsbError(error, runValue);
      }
    } finally {
      UserService.set(UserService.KEY.SIGNING, '');
    }
  }

  private async runModernUsbFlow(runValue: RunValue): Promise<void> {
    const commonUrl = `${window.location.origin}/api/rest/s1/iam-common`;

    try {
      const data = {
        signingType: runValue.actionType,
        idValue: runValue.idValue,
        taxCode: runValue.taxCode || UserService.getUser().partyTaxId,
        responseContentType: 'application/json'
      };
      const signingResult = await firstValueFrom(this.http.put(`${commonUrl}/signing`, data, this.getHttpOptions()));

      this.displaySubject.next('form3');
      const response: any = await firstValueFrom(this.http.post('http://localhost:13369/signing', signingResult));

      if (response?.success) {
        const completeResult: any = await firstValueFrom(this.http.post(`${commonUrl}/complete-signing`, response, this.getHttpOptions()));
        this.processFinalResult(completeResult);
      } else if (response?.errorMessage) {
        this.showErrorDialog(response);
        await this.completeSigning(runValue, 'N');
        this.runReject?.(response);
      }
    } catch (error: any) {
      this.showErrorDialog(error?.error);
      this.handleFinalError();
      this.runReject?.(error);
      throw error;
    }
  }

  private async runLegacyUsbFlow(runValue: RunValue): Promise<void> {
    let loginKey = '';
    if (runValue.requireLoginKey !== false) {
      const apiKey: any = await firstValueFrom(this.loginService.getApiKey());
      loginKey = apiKey.token;
    }

    const data = {
      baseUrl: window.location.origin + '/api',
      loginKey,
      csrfToken: AppService.get(AppService.KEY.MOQUI_SESSION_TOKEN),
      actionType: runValue.actionType,
      idValue: runValue.idValue,
      taxCode: runValue.taxCode || UserService.getUser().partyTaxId
    };

    try {
      const response: any = await firstValueFrom(this.http.post('http://localhost:13369/signing', data));
      if (response?.success) {
        AppSubject.setReloadData('success');
        this.runResolve?.();
      } else if (response?.errorMessage) {
        this.showErrorDialog(response);
        AppSubject.setReloadData('error');
        this.runReject?.(response);
      }
    } catch (error) {
      this.errorSubject.next(error);
      this.runReject?.(error);
      throw error;
    }
  }

  private async handleUsbError(error: any, runValue: RunValue): Promise<void> {
    await this.completeSigning(runValue, 'N');
    this.errorSubject.next(error);
    this.runReject?.(error);
  }

  private async completeSigning(runValue: RunValue, success: string): Promise<void> {
    const url = `${window.location.origin}/api/rest/s1/iam-common/complete-signing`;
    const input = {
      success,
      idValue: runValue.idValue,
      signingType: runValue.actionType
    };
    try {
      const result: any = await firstValueFrom(this.http.post(url, input, this.getHttpOptions()));
      if (result?.idValue) {
        AppSubject.setReloadData(success === 'Y' ? 'success' : 'error');
        this.displaySubject.next('closeForm3');
        if (success === 'Y') {
          this.runResolve?.();
        } else {
          this.runReject?.(result);
        }
      }
    } catch (error) {
      this.runReject?.(error);
    }
  }

  private async handleRemoteSigningFlow(signingType: string): Promise<void> {
    const url = `${window.location.origin}/api/rest/s1/iam-common/remote-signing?partyClassificationId=${signingType}`;
    try {
      const result: any = await firstValueFrom(this.http.get(url, this.getHttpOptions()));
      if (result.listData) {
        UserService.set(UserService.KEY.REMOTE_SIGNING, JSON.stringify(result.listData));
        this.displaySubject.next('form2');
      }
    } catch (error) {
      this.runReject?.(error);
    }
  }

  private processFinalResult(result: any): void {
    if (result?.idValue) {
      AppSubject.setReloadData('success');
      this.displaySubject.next('closeForm3');
      this.runResolve?.();
    } else if (result?.errorMessage) {
      this.showErrorDialog(result);
      this.handleFinalError();
      this.runReject?.(result);
    }
  }

  private handleFinalError(): void {
    AppSubject.setReloadData('error');
    this.displaySubject.next('closeForm3');
  }

  private showErrorDialog(error: any): void {
    if (!error) {
      return;
    }
    this.confirmService.setConfirmDialog({
      message: `[${error.errorCode || 'Error'}] ${error.errorMessage || error.errors || 'Unknown error'}`,
      header: 'Ký điện tử không thành công',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Đóng',
      acceptVisible: false
    });
  }

  private getHttpOptions(): any {
    return {
      headers: { 'x-csrf-token': AppService.get(AppService.KEY.MOQUI_SESSION_TOKEN) }
    };
  }

  remoteSigning(data: any): Observable<any> {
    const obs = this.http.post('/api/rest/s1/iam-common/remote-signing', data, this.getHttpOptions());
    return new Observable(subscriber => {
      obs.subscribe({
        next: (val) => {
          this.runResolve?.();
          subscriber.next(val);
        },
        error: (err) => {
          this.runReject?.(err);
          subscriber.error(err);
        },
        complete: () => subscriber.complete()
      });
    });
  }
}
